
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ScrollText } from 'lucide-react';
import { ScriptureReference } from './types';

interface ScriptureReferencesProps {
  references: ScriptureReference[];
  contextualTitle?: string;
}

export const ScriptureReferences: React.FC<ScriptureReferencesProps> = ({ 
  references,
  contextualTitle 
}) => {
  // Validate input first
  if (!references || !Array.isArray(references) || references.length === 0) {
    console.log("No valid references array to display:", references);
    return null;
  }
  
  // Enhanced processing of references to ensure they're complete
  const processedReferences = references.map(ref => {
    if (!ref) return null;
    
    // Ensure we have a valid reference string
    const reference = ref.reference ? ref.reference.trim() : '';
    if (!reference) return null;
    
    // Ensure we have text content
    const text = ref.text ? ref.text.trim() : '';
    if (!text) return null;
    
    // Always include an explanation, even if just default text
    const explanation = ref.explanation && ref.explanation.trim() 
      ? ref.explanation.trim() 
      : "This verse is relevant to understanding the Christian perspective on this topic.";
    
    return { reference, text, explanation };
  }).filter(Boolean);
  
  // If no valid references after processing, don't render anything
  if (processedReferences.length === 0) {
    console.log("No valid scripture references after processing:", references);
    return null;
  }

  return (
    <Card className="border-heaven-gold/10">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-heaven-gold text-lg">
          <ScrollText className="h-5 w-5" />
          <span>{contextualTitle || "Scripture References"}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {processedReferences.map((scripture, i) => (
            <div key={i} className="p-3 bg-heaven-gold/5 rounded text-left">
              <p className="font-semibold text-heaven-gold mb-1">
                {scripture.reference}
              </p>
              <p className="italic text-gray-700 mb-2">
                {scripture.text.startsWith('"') && scripture.text.endsWith('"') 
                  ? scripture.text 
                  : `"${scripture.text}"`}
              </p>
              <p className="text-sm text-gray-600 mt-1">{scripture.explanation}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
